import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAppSelector } from '@store/store.tsx';
import { selectSiteInfo, selectSiteInfoStatus } from '@store/slices/settingsSlice.tsx';

const PRIVATE_ESHOP_PREFIXES = ['/eshop/dashboard', '/eshop/account'] as const;

const hasPathPrefix = (pathname: string, prefix: string): boolean => (
  pathname === prefix || pathname.startsWith(`${prefix}/`)
);

/** Returns whether a route can represent a missing public-site URL. */
export const isPublicNotFoundPath = (pathname: string): boolean => {
  const cleanPath = pathname.split('?')[0].split('#')[0];

  if (cleanPath.startsWith('/web/')) {
    return true;
  }

  if (!cleanPath.startsWith('/eshop/')) {
    return false;
  }

  return !PRIVATE_ESHOP_PREFIXES.some((prefix) => hasPathPrefix(cleanPath, prefix));
};

export interface PublicNotFoundRedirectProps {
  fallback?: ReactNode;
  enabled?: boolean;
}

/**
 * Redirects a public 404 to the canonical site entry point only after the
 * current host has been confirmed by the public site-info request.
 */
const PublicNotFoundRedirect = ({
  fallback = <div>404 - Página no encontrada</div>,
  enabled = true,
}: PublicNotFoundRedirectProps) => {
  const { pathname } = useLocation();
  const siteInfo = useAppSelector(selectSiteInfo);
  const siteInfoStatus = useAppSelector(selectSiteInfoStatus);

  if (
    enabled
    && isPublicNotFoundPath(pathname)
    && siteInfoStatus === 'idle'
    && siteInfo !== null
  ) {
    return <Navigate to="/" replace />;
  }

  return <>{fallback}</>;
};

export default PublicNotFoundRedirect;
